import React, { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  ClipboardCheck,
  Building2,
  Calendar,
  FileDown,
  Save,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Users,
  FileText,
  Loader2,
  ListChecks,
  ArrowRight,
  UserCheck,
  Gavel,
  AlertCircle,
  Trash2,
  RotateCcw,
  Edit,
  ShieldAlert,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FR48DecisionPage = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [audits, setAudits] = useState([]);
  const [approvedStage3Audits, setApprovedStage3Audits] = useState([]);
  const [loadingApproved, setLoadingApproved] = useState(true);
  const [selectedAuditId, setSelectedAuditId] = useState("");
  const [fr48Data, setFr48Data] = useState(null);
  const [fr48List, setFr48List] = useState([]);
  const [activeTab, setActiveTab] = useState("technical");

  // Rol kontrolü
  const userRole = user?.role || "";
  const userRoles = user?.roles || [userRole];
  
  // Admin kontrolü
  const isAdmin = userRoles.includes("super_admin") || userRoles.includes("admin");
  
  const isTechnicalReviewer = userRoles.includes("technical_reviewer") || 
                              userRoles.includes("super_admin") || 
                              userRoles.includes("admin") ||
                              userRoles.includes("certificate_responsible");
  
  const isCommitteeMember = userRoles.includes("certification_committee") || 
                            userRoles.includes("super_admin") || 
                            userRoles.includes("admin") ||
                            userRoles.includes("certificate_responsible");
  
  // FR.48 durumu
  const technicalReviewCompleted = fr48Data?.technical_review_status === "completed";
  const committeeReviewCompleted = fr48Data?.committee_review_status === "completed" || fr48Data?.status === "approved" || fr48Data?.status === "rejected";

  // Form state
  const [formData, setFormData] = useState({
    // Karar türü
    decision_type: "certification", // certification, continuation, change
    
    // Kuruluş bilgileri (otomatik dolar)
    organization_name: "",
    address: "",
    scope: "GSTC INDUSTRY CRITERIA HOTELS AND ACCOMODATIONS",
    certificate_no: "",
    gstc_certification_code: "",
    audit_date: "",
    certificate_issue_date: "",
    recertification_date: "",
    expiration_date: "",
    
    // Teknik İnceleme
    scope_appropriate: null,
    draft_certificate_complying: null,
    regulatory_compliance_made: null,
    audit_findings_satisfactory: null,
    nonconformity_closure_sufficient: null,
    reasons_if_no: "",
    
    // Teknik İncelemeci
    technical_reviewer_name: "",
    technical_reviewer_date: "",
    
    // Komite Kriterleri
    audit_file_prepared: null,
    reports_demonstrate_conformity: null,
    reports_demonstrate_effectiveness: null,
    corrective_action_efficient: null,
    nonconformities_closed: null,
    requirements_fulfilled: null,
    
    // Denetçi Performansı
    auditor_auditing_performance: "appropriate",
    objective_evidences: "appropriate",
    iaf_md_compliance: "appropriate",
    performance_notes: "",
    
    // Kararlar (default: boş)
    certification_decision: null,
    recertification_decision: null,
    transfer_decision: null,
    continuation_decision: null,
    change_decision: null,
    
    // Sonuç
    result: "successful",
    result_explanation: "",
    
    // Komite Üyesi
    committee_member_name: "",
    committee_member_date: "",
  });

  // Denetimleri yükle - Sadece Stage 3 denetimleri
  const fetchAudits = useCallback(async () => {
    try {
      // Use /api/audits endpoint to get audit info with audit_type
      const response = await axios.get(`${API}/audits`, { withCredentials: true });
      const auditsData = response.data || [];
      
      // FR.48 sadece Stage 3 denetimleri için kullanılıyor, Stage 2 denetimlerini filtrele
      const stage3Audits = auditsData.filter(audit => {
        const auditType = audit.audit_type || "";
        
        // Stage 2 denetimlerini hariç tut (stage3 içerenler veya stage belirtilmemiş olanlar dahil)
        const isStage2 = auditType.includes("stage2") || 
                         auditType.includes("_stage2") ||
                         auditType === "initial_stage2" ||
                         auditType === "surveillance_stage2" ||
                         auditType === "recert_stage2";
        
        // Sadece Stage 3 denetimlerini göster
        const isStage3 = auditType.includes("stage3") || 
                         auditType.includes("_stage3") ||
                         auditType === "initial_stage3" ||
                         auditType === "surveillance_stage3" ||
                         auditType === "recert_stage3";
        
        return isStage3 && !isStage2;
      });
      
      setAudits(stage3Audits);
    } catch (error) {
      console.error("Error fetching audits:", error);
    }
  }, []);

  // FR.48 listesini yükle
  const fetchFR48List = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fr48`, { withCredentials: true });
      setFr48List(response.data.fr48_decisions || []);
    } catch (error) {
      console.error("Error fetching FR.48 list:", error);
    }
  }, []);

  // Onaylı 3. Aşama Denetimlerini yükle (raporu onaylanmış olanlar)
  const fetchApprovedStage3Audits = useCallback(async () => {
    setLoadingApproved(true);
    try {
      // Önce özel approved endpoint'ini dene (stage 3 için)
      try {
        const approvedRes = await axios.get(`${API}/royalcert-audits/approved/list?stage=3`, { withCredentials: true });
        if (approvedRes.data && approvedRes.data.length > 0) {
          setApprovedStage3Audits(approvedRes.data);
          setLoadingApproved(false);
          return;
        }
      } catch (e) {
        console.log("Approved list endpoint failed, trying main endpoint");
      }

      // RoyalCert audits endpoint'inden onaylı Stage 3 denetimlerini çek
      const response = await axios.get(`${API}/royalcert-audits`, { withCredentials: true });
      const allAudits = response.data || [];
      
      // Sadece Stage 3 ve raporu onaylanmış olanları filtrele
      const approved = allAudits.filter(audit => {
        // Stage 3 kontrolü - is_stage2 false olmalı veya template_id stage3 içermeli
        const isStage3 = audit.is_stage2 === false || 
                         !audit.is_stage2 ||
                         audit.template_id?.includes('stage3') ||
                         audit.audit_type?.includes('stage3') || 
                         audit.audit_type?.includes('_3') ||
                         audit.audit_type === 'initial_stage3' ||
                         audit.audit_type === 'surveillance_stage3' ||
                         audit.audit_type === 'recert_stage3';
        
        // Onay kontrolü - review_status veya report_status veya status
        const isApproved = audit.review_status === 'approved' || 
                          audit.report_status === 'approved' || 
                          audit.status === 'approved';
        
        // Stage 2 değilse ve onaylıysa
        const notStage2 = !audit.is_stage2 && 
                          !audit.template_id?.includes('stage2') &&
                          !audit.audit_type?.includes('stage2');
        
        return isStage3 && isApproved && notStage2;
      });
      
      setApprovedStage3Audits(approved);
    } catch (error) {
      console.error("Error fetching approved audits:", error);
      // Fallback: Normal audits endpoint'ini kullan
      try {
        const response = await axios.get(`${API}/audits`, { withCredentials: true });
        const auditsData = response.data || [];
        const approved = auditsData.filter(audit => {
          const isStage3 = audit.audit_type?.includes('stage3');
          const isCompleted = audit.status === 'completed';
          return isStage3 && isCompleted;
        });
        setApprovedStage3Audits(approved);
      } catch (err) {
        console.error("Fallback also failed:", err);
      }
    } finally {
      setLoadingApproved(false);
    }
  }, []);

  useEffect(() => {
    fetchAudits();
    fetchFR48List();
    fetchApprovedStage3Audits();
  }, [fetchAudits, fetchFR48List, fetchApprovedStage3Audits]);

  // Denetim seçildiğinde bilgileri doldur
  const handleAuditSelect = async (auditId) => {
    setSelectedAuditId(auditId);
    setLoading(true);

    try {
      // Mevcut FR.48 var mı kontrol et
      const fr48Response = await axios.get(`${API}/audits/${auditId}/fr48`, { withCredentials: true });
      
      if (fr48Response.data) {
        // Mevcut FR.48 varsa yükle
        const fr48 = fr48Response.data;
        setFr48Data(fr48);
        setFormData({
          decision_type: fr48.decision_type || "certification",
          organization_name: fr48.organization_name || "",
          address: fr48.organization_address || "",
          scope: fr48.scope || "GSTC INDUSTRY CRITERIA HOTELS AND ACCOMODATIONS",
          certificate_no: fr48.certificate_no || "",
          gstc_certification_code: fr48.gstc_certification_code || "",
          audit_date: fr48.audit_date || "",
          certificate_issue_date: fr48.certificate_issue_date || "",
          recertification_date: fr48.recertification_date || "",
          expiration_date: fr48.expiration_date || "",
          scope_appropriate: fr48.technical_review?.scope_appropriate ?? null,
          draft_certificate_complying: fr48.technical_review?.draft_certificate_complying ?? null,
          regulatory_compliance_made: fr48.technical_review?.regulatory_compliance_made ?? null,
          audit_findings_satisfactory: fr48.technical_review?.audit_findings_satisfactory ?? null,
          nonconformity_closure_sufficient: fr48.technical_review?.nonconformity_closure_sufficient ?? null,
          reasons_if_no: fr48.technical_review?.reasons_if_no || "",
          technical_reviewer_name: fr48.technical_reviewer_name || "",
          technical_reviewer_date: fr48.technical_reviewer_date || "",
          audit_file_prepared: fr48.committee_criteria?.audit_file_prepared ?? null,
          reports_demonstrate_conformity: fr48.committee_criteria?.reports_demonstrate_conformity ?? null,
          reports_demonstrate_effectiveness: fr48.committee_criteria?.reports_demonstrate_effectiveness ?? null,
          corrective_action_efficient: fr48.committee_criteria?.corrective_action_efficient ?? null,
          nonconformities_closed: fr48.committee_criteria?.nonconformities_closed ?? null,
          requirements_fulfilled: fr48.committee_criteria?.requirements_fulfilled ?? null,
          auditor_auditing_performance: fr48.auditor_performance?.auditor_auditing_performance || "appropriate",
          objective_evidences: fr48.auditor_performance?.objective_evidences || "appropriate",
          iaf_md_compliance: fr48.auditor_performance?.iaf_md_compliance || "appropriate",
          performance_notes: fr48.auditor_performance?.notes || "",
          certification_decision: fr48.certification_decision || "acceptable",
          recertification_decision: fr48.recertification_decision,
          transfer_decision: fr48.transfer_decision,
          continuation_decision: fr48.continuation_decision,
          change_decision: fr48.change_decision,
          result: fr48.result || "successful",
          result_explanation: fr48.result_explanation || "",
          committee_member_name: fr48.committee_members?.[0]?.name || "",
          committee_member_date: fr48.committee_members?.[0]?.date || "",
        });
      } else {
        // Yeni FR.48 - denetim bilgilerini al
        const auditResponse = await axios.get(`${API}/audits/${auditId}`, { withCredentials: true });
        const audit = auditResponse.data;
        
        // Müşteri bilgilerini al
        let customer = {};
        if (audit.customer_id) {
          try {
            const customerResponse = await axios.get(`${API}/customers/${audit.customer_id}`, { withCredentials: true });
            customer = customerResponse.data || {};
          } catch (e) {
            console.error("Customer fetch error:", e);
          }
        }

        const address = [customer.address, customer.city, customer.province].filter(Boolean).join(", ");
        
        setFr48Data(null);
        setFormData(prev => ({
          ...prev,
          organization_name: customer.brand_name || customer.name || audit.customer_name || "",
          address: address,
          certificate_no: audit.certificate_number || "",
          gstc_certification_code: audit.gstc_certification_code || "",
          audit_date: audit.start_date || "",
          certificate_issue_date: new Date().toISOString().split('T')[0],
        }));
      }
    } catch (error) {
      console.error("Error loading audit data:", error);
      toast.error("Denetim bilgileri yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  // Formu kaydet
  const handleSave = async () => {
    if (!selectedAuditId) {
      toast.error("Lütfen bir denetim seçin");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        audit_id: selectedAuditId,
        decision_type: formData.decision_type,
        organization_name: formData.organization_name,
        organization_address: formData.address,
        scope: formData.scope,
        certificate_no: formData.certificate_no,
        gstc_certification_code: formData.gstc_certification_code,
        audit_date: formData.audit_date,
        certificate_issue_date: formData.certificate_issue_date,
        recertification_date: formData.recertification_date,
        expiration_date: formData.expiration_date,
        technical_review: {
          scope_appropriate: formData.scope_appropriate,
          draft_certificate_complying: formData.draft_certificate_complying,
          regulatory_compliance_made: formData.regulatory_compliance_made,
          audit_findings_satisfactory: formData.audit_findings_satisfactory,
          nonconformity_closure_sufficient: formData.nonconformity_closure_sufficient,
          reasons_if_no: formData.reasons_if_no,
        },
        technical_reviewer_name: formData.technical_reviewer_name,
        technical_reviewer_date: formData.technical_reviewer_date,
        committee_criteria: {
          audit_file_prepared: formData.audit_file_prepared,
          reports_demonstrate_conformity: formData.reports_demonstrate_conformity,
          reports_demonstrate_effectiveness: formData.reports_demonstrate_effectiveness,
          corrective_action_efficient: formData.corrective_action_efficient,
          nonconformities_closed: formData.nonconformities_closed,
          requirements_fulfilled: formData.requirements_fulfilled,
        },
        auditor_performance: {
          auditor_auditing_performance: formData.auditor_auditing_performance,
          objective_evidences: formData.objective_evidences,
          iaf_md_compliance: formData.iaf_md_compliance,
          notes: formData.performance_notes,
        },
        certification_decision: formData.certification_decision,
        recertification_decision: formData.recertification_decision,
        transfer_decision: formData.transfer_decision,
        continuation_decision: formData.continuation_decision,
        change_decision: formData.change_decision,
        result: formData.result,
        result_explanation: formData.result_explanation,
        committee_members: formData.committee_member_name ? [{
          name: formData.committee_member_name,
          date: formData.committee_member_date || new Date().toISOString().split('T')[0],
          role: "Member"
        }] : [],
      };

      if (fr48Data) {
        // Güncelle
        await axios.put(`${API}/fr48/${fr48Data.fr48_id}/save`, payload, { withCredentials: true });
        toast.success("FR.48 güncellendi");
      } else {
        // Yeni oluştur
        const response = await axios.post(`${API}/fr48/create-full`, payload, { withCredentials: true });
        setFr48Data(response.data);
        toast.success("FR.48 kaydedildi");
      }
      
      fetchFR48List();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kaydetme hatası");
    } finally {
      setSaving(false);
    }
  };

  // Teknik İncelemeyi Tamamla
  const handleSaveTechnicalReview = async () => {
    if (!selectedAuditId) {
      toast.error("Lütfen bir denetim seçin");
      return;
    }

    // Validasyon
    if (!formData.technical_reviewer_name) {
      toast.error("Teknik İncelemeci adı gerekli");
      return;
    }

    setSaving(true);
    try {
      // Önce FR.48 kaydını oluştur veya güncelle
      let fr48Id = fr48Data?.fr48_id;
      if (!fr48Id) {
        // Yeni oluştur
        const createPayload = {
          audit_id: selectedAuditId,
          decision_type: formData.decision_type,
          organization_name: formData.organization_name,
          organization_address: formData.address,
          scope: formData.scope,
          certificate_no: formData.certificate_no,
          gstc_certification_code: formData.gstc_certification_code,
          audit_date: formData.audit_date,
          certificate_issue_date: formData.certificate_issue_date,
          recertification_date: formData.recertification_date,
          expiration_date: formData.expiration_date,
        };
        const createResponse = await axios.post(`${API}/fr48/create-full`, createPayload, { withCredentials: true });
        fr48Id = createResponse.data.fr48_id;
        setFr48Data(createResponse.data);
      }

      // Teknik incelemeyi tamamla
      const technicalPayload = {
        technical_review: {
          scope_appropriate: formData.scope_appropriate,
          draft_certificate_complying: formData.draft_certificate_complying,
          regulatory_compliance_made: formData.regulatory_compliance_made,
          audit_findings_satisfactory: formData.audit_findings_satisfactory,
          nonconformity_closure_sufficient: formData.nonconformity_closure_sufficient,
          reasons_if_no: formData.reasons_if_no,
        },
        technical_reviewer_name: formData.technical_reviewer_name,
      };

      await axios.put(`${API}/fr48/${fr48Id}/technical-review`, technicalPayload, { withCredentials: true });
      
      // FR.48 verisini yenile
      const updatedFr48 = await axios.get(`${API}/fr48/${fr48Id}`, { withCredentials: true });
      setFr48Data(updatedFr48.data);
      
      toast.success("Teknik İnceleme tamamlandı. Komite onayı bekliyor.");
      setActiveTab("committee");
      fetchFR48List();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Teknik inceleme kaydetme hatası");
    } finally {
      setSaving(false);
    }
  };

  // Komite Kararını Kaydet
  const handleSaveCommitteeReview = async () => {
    if (!fr48Data?.fr48_id) {
      toast.error("Önce FR.48 kaydı oluşturulmalı");
      return;
    }

    // Validasyon
    if (!formData.committee_member_name) {
      toast.error("Komite üyesi adı gerekli");
      return;
    }

    setSaving(true);
    try {
      const committeePayload = {
        committee_criteria: {
          audit_file_prepared: formData.audit_file_prepared,
          reports_demonstrate_conformity: formData.reports_demonstrate_conformity,
          reports_demonstrate_effectiveness: formData.reports_demonstrate_effectiveness,
          corrective_action_efficient: formData.corrective_action_efficient,
          nonconformities_closed: formData.nonconformities_closed,
          requirements_fulfilled: formData.requirements_fulfilled,
        },
        auditor_performance: {
          auditor_auditing_performance: formData.auditor_auditing_performance,
          objective_evidences: formData.objective_evidences,
          iaf_md_compliance: formData.iaf_md_compliance,
          notes: formData.performance_notes,
        },
        certification_decision: formData.certification_decision,
        recertification_decision: formData.recertification_decision,
        transfer_decision: formData.transfer_decision,
        continuation_decision: formData.continuation_decision,
        change_decision: formData.change_decision,
        result: formData.result,
        result_explanation: formData.result_explanation,
        committee_members: [{
          name: formData.committee_member_name,
          date: formData.committee_member_date || new Date().toISOString().split('T')[0],
          role: "Member"
        }],
      };

      await axios.put(`${API}/fr48/${fr48Data.fr48_id}/committee-review`, committeePayload, { withCredentials: true });
      
      // FR.48 verisini yenile
      const updatedFr48 = await axios.get(`${API}/fr48/${fr48Data.fr48_id}`, { withCredentials: true });
      setFr48Data(updatedFr48.data);
      
      const resultMsg = formData.result === "successful" ? "ONAYLANDI" : "REDDEDİLDİ";
      toast.success(`Komite kararı kaydedildi: ${resultMsg}`);
      fetchFR48List();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Komite kararı kaydetme hatası");
    } finally {
      setSaving(false);
    }
  };

  // Formu indir
  const handleDownload = async () => {
    if (!selectedAuditId) {
      toast.error("Lütfen bir denetim seçin");
      return;
    }

    setDownloading(true);
    try {
      // Önce kaydet
      await handleSave();
      
      // Sonra indir
      const response = await axios.get(
        `${API}/fr48/download/${fr48Data?.fr48_id || selectedAuditId}`,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FR48_${formData.organization_name || 'form'}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("FR.48 indirildi");
    } catch (error) {
      // Blob response hatası için özel handling
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const jsonError = JSON.parse(text);
          toast.error(jsonError.detail || "İndirme hatası");
        } catch {
          toast.error("İndirme hatası");
        }
      } else {
        toast.error(error.response?.data?.detail || "İndirme hatası");
      }
    } finally {
      setDownloading(false);
    }
  };

  // Admin: Komite Onayını Kaldır
  const handleRevokeApproval = async () => {
    if (!fr48Data?.fr48_id) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/fr48/${fr48Data.fr48_id}/revoke-approval`, {}, { withCredentials: true });
      toast.success("Komite onayı kaldırıldı");
      
      // Veriyi yenile
      const response = await axios.get(`${API}/fr48/${fr48Data.fr48_id}`, { withCredentials: true });
      setFr48Data(response.data);
      fetchFR48List();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Onay kaldırma hatası");
    } finally {
      setSaving(false);
    }
  };

  // Admin: Teknik İncelemeyi Kaldır
  const handleRevokeTechnicalReview = async () => {
    if (!fr48Data?.fr48_id) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/fr48/${fr48Data.fr48_id}/revoke-technical-review`, {}, { withCredentials: true });
      toast.success("Teknik inceleme kaldırıldı");
      
      // Veriyi yenile
      const response = await axios.get(`${API}/fr48/${fr48Data.fr48_id}`, { withCredentials: true });
      setFr48Data(response.data);
      fetchFR48List();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Teknik inceleme kaldırma hatası");
    } finally {
      setSaving(false);
    }
  };

  // Admin: FR.48 Sil
  const handleDeleteFR48 = async () => {
    if (!fr48Data?.fr48_id) return;
    
    setSaving(true);
    try {
      await axios.delete(`${API}/fr48/${fr48Data.fr48_id}`, { withCredentials: true });
      toast.success("FR.48 silindi");
      
      // Formu sıfırla
      setFr48Data(null);
      setSelectedAuditId("");
      resetForm();
      fetchFR48List();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Silme hatası");
    } finally {
      setSaving(false);
    }
  };

  // Yes/No seçici bileşen
  const YesNoSelector = ({ label, value, onChange, required, disabled }) => (
    <div className={`flex items-center justify-between p-3 border rounded-lg ${disabled ? 'bg-muted/50 opacity-60' : 'bg-muted/30'}`}>
      <span className="text-sm flex-1">{label}</span>
      <div className="flex gap-2">
        <Button
          type="button"
          variant={value === true ? "default" : "outline"}
          size="sm"
          onClick={() => !disabled && onChange(true)}
          disabled={disabled}
        >
          Yes
        </Button>
        <Button
          type="button"
          variant={value === false ? "destructive" : "outline"}
          size="sm"
          onClick={() => !disabled && onChange(false)}
          disabled={disabled}
        >
          No
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 p-6" data-testid="fr48-decision-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6 text-primary" />
            FR.48 Teknik İnceleme ve Karar Formu
          </h1>
          <p className="text-muted-foreground">
            Sertifika kararı için teknik inceleme ve komite onayı
          </p>
        </div>
      </div>

      {/* 3. Aşama Onaylı Denetimler Tablosu */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-green-600" />
            3. Aşama Onaylı Denetimler
          </CardTitle>
          <CardDescription>
            Raporu onaylanmış 3. Aşama (GSTC) denetimleri - FR.48 doldurmak için birini seçin
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingApproved ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : approvedStage3Audits.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Henüz raporu onaylanmış 3. Aşama denetimi bulunmuyor
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Tesis Adı</TableHead>
                    <TableHead>Denetim Tarihi</TableHead>
                    <TableHead>Denetçi</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead>FR.48</TableHead>
                    <TableHead className="text-right">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {approvedStage3Audits.map((audit) => {
                    const hasFR48 = fr48List.some(f => f.audit_id === audit.audit_id || f.audit_id === audit._id);
                    const fr48Status = fr48List.find(f => f.audit_id === audit.audit_id || f.audit_id === audit._id)?.status;
                    
                    return (
                      <TableRow 
                        key={audit.audit_id || audit._id}
                        className={selectedAuditId === (audit.audit_id || audit._id) ? "bg-primary/10" : "hover:bg-muted/30"}
                      >
                        <TableCell className="font-medium">
                          {audit.customer_name || audit.facility_name || audit.title || "-"}
                        </TableCell>
                        <TableCell>
                          {audit.scheduled_date || audit.audit_date || audit.audit_start_date || audit.created_at
                            ? new Date(audit.scheduled_date || audit.audit_date || audit.audit_start_date || audit.created_at).toLocaleDateString('tr-TR')
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {audit.lead_auditor_name || audit.assigned_auditor_name || audit.auditor_name || audit.auditor || "-"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="success" className="bg-green-100 text-green-800">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Onaylı
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {hasFR48 ? (
                            <Badge variant={fr48Status === "approved" ? "success" : "secondary"}>
                              {fr48Status === "approved" ? "Tamamlandı" : "Taslak"}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">
                              Bekliyor
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant={selectedAuditId === (audit.audit_id || audit._id) ? "default" : "outline"}
                            onClick={() => handleAuditSelect(audit.audit_id || audit._id)}
                          >
                            {selectedAuditId === (audit.audit_id || audit._id) ? (
                              <>Seçili</>
                            ) : (
                              <>
                                Seç <ArrowRight className="h-4 w-4 ml-1" />
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : selectedAuditId ? (
        <>
          {/* İş Akışı Durumu */}
          <Card className="border-2 border-primary/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-8">
                  {/* Adım 1: Teknik İnceleme */}
                  <div className="flex items-center gap-2">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      technicalReviewCompleted 
                        ? "bg-green-100 text-green-600" 
                        : "bg-amber-100 text-amber-600"
                    }`}>
                      {technicalReviewCompleted ? <CheckCircle2 className="h-5 w-5" /> : <UserCheck className="h-5 w-5" />}
                    </div>
                    <div>
                      <p className="font-medium text-sm">1. Teknik İnceleme</p>
                      <p className="text-xs text-muted-foreground">
                        {technicalReviewCompleted ? "Tamamlandı" : "Bekliyor"}
                      </p>
                    </div>
                  </div>
                  
                  <ArrowRight className="h-5 w-5 text-muted-foreground" />
                  
                  {/* Adım 2: Komite Kararı */}
                  <div className="flex items-center gap-2">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      committeeReviewCompleted 
                        ? fr48Data?.status === "approved" ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
                        : technicalReviewCompleted ? "bg-amber-100 text-amber-600" : "bg-gray-100 text-gray-400"
                    }`}>
                      {committeeReviewCompleted 
                        ? fr48Data?.status === "approved" ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />
                        : <Gavel className="h-5 w-5" />
                      }
                    </div>
                    <div>
                      <p className="font-medium text-sm">2. Komite Kararı</p>
                      <p className="text-xs text-muted-foreground">
                        {committeeReviewCompleted 
                          ? fr48Data?.status === "approved" ? "Onaylandı" : "Reddedildi"
                          : technicalReviewCompleted ? "Bekliyor" : "Teknik inceleme bekleniyor"
                        }
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Mevcut Durum Badge */}
                <Badge variant={
                  fr48Data?.status === "approved" ? "success" :
                  fr48Data?.status === "rejected" ? "destructive" :
                  fr48Data?.status === "committee_pending" ? "warning" :
                  "secondary"
                } className="text-sm px-3 py-1">
                  {fr48Data?.status === "approved" ? "Onaylandı" :
                   fr48Data?.status === "rejected" ? "Reddedildi" :
                   fr48Data?.status === "committee_pending" ? "Komite Bekliyor" :
                   fr48Data?.status === "technical_review" ? "Teknik İnceleme" :
                   "Taslak"
                  }
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Admin Yönetim Paneli */}
          {isAdmin && fr48Data && (
            <Card className="border-orange-200 bg-orange-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-orange-800">
                  <ShieldAlert className="h-5 w-5" />
                  Admin Yönetim Paneli
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {/* Komite Onayını Kaldır */}
                  {committeeReviewCompleted && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="text-amber-700 border-amber-300 hover:bg-amber-50">
                          <RotateCcw className="h-4 w-4 mr-2" />
                          Komite Onayını Kaldır
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Komite Onayını Kaldır</AlertDialogTitle>
                          <AlertDialogDescription>
                            Bu işlem FR.48 komite onayını kaldıracak ve komite kararı yeniden verilebilir hale gelecektir.
                            <br /><br />
                            <strong>Kuruluş:</strong> {formData.organization_name}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>İptal</AlertDialogCancel>
                          <AlertDialogAction onClick={handleRevokeApproval} className="bg-amber-600 hover:bg-amber-700">
                            Onayı Kaldır
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}

                  {/* Teknik İncelemeyi Kaldır */}
                  {technicalReviewCompleted && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="text-orange-700 border-orange-300 hover:bg-orange-50">
                          <RotateCcw className="h-4 w-4 mr-2" />
                          Teknik İncelemeyi Kaldır
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Teknik İncelemeyi Kaldır</AlertDialogTitle>
                          <AlertDialogDescription>
                            Bu işlem FR.48 teknik incelemesini ve varsa komite kararını da kaldıracaktır. Form baştan doldurulabilir hale gelecektir.
                            <br /><br />
                            <strong>Kuruluş:</strong> {formData.organization_name}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>İptal</AlertDialogCancel>
                          <AlertDialogAction onClick={handleRevokeTechnicalReview} className="bg-orange-600 hover:bg-orange-700">
                            Kaldır
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}

                  {/* FR.48 Sil */}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="text-red-700 border-red-300 hover:bg-red-50">
                        <Trash2 className="h-4 w-4 mr-2" />
                        FR.48 Sil
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>FR.48 Silme Onayı</AlertDialogTitle>
                        <AlertDialogDescription>
                          Bu işlem FR.48 kaydını kalıcı olarak silecektir. Bu işlem geri alınamaz!
                          <br /><br />
                          <strong>Kuruluş:</strong> {formData.organization_name}
                          <br />
                          <strong>Durum:</strong> {fr48Data?.status || "Taslak"}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>İptal</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteFR48} className="bg-red-600 hover:bg-red-700">
                          Sil
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sekmeli Form Yapısı */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="technical" className="flex items-center gap-2">
                <UserCheck className="h-4 w-4" />
                Teknik İnceleme
                {technicalReviewCompleted && <CheckCircle2 className="h-4 w-4 text-green-600" />}
              </TabsTrigger>
              <TabsTrigger 
                value="committee" 
                className="flex items-center gap-2"
                disabled={!technicalReviewCompleted && !isCommitteeMember}
              >
                <Gavel className="h-4 w-4" />
                Komite Kararı
                {committeeReviewCompleted && <CheckCircle2 className="h-4 w-4 text-green-600" />}
              </TabsTrigger>
            </TabsList>

            {/* Teknik İnceleme Sekmesi */}
            <TabsContent value="technical" className="space-y-6">
              {!isTechnicalReviewer && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-amber-800">
                      <AlertCircle className="h-5 w-5" />
                      <span>Bu bölümü düzenlemek için Teknik İncelemeci yetkisi gereklidir.</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Karar Türü */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Karar Türü</CardTitle>
                </CardHeader>
                <CardContent>
                  <RadioGroup
                    value={formData.decision_type}
                    onValueChange={(v) => setFormData({ ...formData, decision_type: v })}
                    className="flex flex-wrap gap-4"
                    disabled={!isTechnicalReviewer}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="certification" id="certification" />
                      <Label htmlFor="certification">Certification Decision</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="continuation" id="continuation" />
                      <Label htmlFor="continuation">Continuation Decision</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="change" id="change" />
                      <Label htmlFor="change">Change Decision</Label>
                    </div>
                  </RadioGroup>
                </CardContent>
              </Card>

              {/* Kuruluş Bilgileri */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Kuruluş Bilgileri
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Organization Name</Label>
                    <Input
                      value={formData.organization_name}
                      onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                      disabled={!isTechnicalReviewer || technicalReviewCompleted}
                    />
                  </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  disabled={!isTechnicalReviewer || technicalReviewCompleted}
                />
              </div>
              <div className="space-y-2">
                <Label>Certificate No</Label>
                <Input
                  value={formData.certificate_no}
                  onChange={(e) => setFormData({ ...formData, certificate_no: e.target.value })}
                  disabled={!isTechnicalReviewer || technicalReviewCompleted}
                />
              </div>
              <div className="space-y-2">
                <Label>GSTC Certification Code</Label>
                <Input
                  value={formData.gstc_certification_code}
                  onChange={(e) => setFormData({ ...formData, gstc_certification_code: e.target.value })}
                  disabled={!isTechnicalReviewer || technicalReviewCompleted}
                />
              </div>
              <div className="space-y-2">
                <Label>Audit Date</Label>
                <Input
                  type="date"
                  value={formData.audit_date}
                  onChange={(e) => setFormData({ ...formData, audit_date: e.target.value })}
                  disabled={!isTechnicalReviewer || technicalReviewCompleted}
                />
              </div>
              <div className="space-y-2">
                <Label>Certificate Issue Date</Label>
                <Input
                  type="date"
                  value={formData.certificate_issue_date}
                  onChange={(e) => setFormData({ ...formData, certificate_issue_date: e.target.value })}
                  disabled={!isTechnicalReviewer || technicalReviewCompleted}
                />
              </div>
              <div className="space-y-2">
                <Label>Re-certification Date</Label>
                <Input
                  type="date"
                  value={formData.recertification_date}
                  onChange={(e) => setFormData({ ...formData, recertification_date: e.target.value })}
                  disabled={!isTechnicalReviewer || technicalReviewCompleted}
                />
              </div>
              <div className="space-y-2">
                <Label>Expiration Date</Label>
                <Input
                  type="date"
                  value={formData.expiration_date}
                  onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                  disabled={!isTechnicalReviewer || technicalReviewCompleted}
                />
              </div>
            </CardContent>
          </Card>

          {/* Teknik İnceleme */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Technical Review of Audit
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <YesNoSelector
                label="Is Scope of Certificate appropriate for GSTC programme?"
                value={formData.scope_appropriate}
                onChange={(v) => setFormData({ ...formData, scope_appropriate: v })}
                disabled={!isTechnicalReviewer || technicalReviewCompleted}
              />
              <YesNoSelector
                label="Is content of draft certificate complying with GSTC scheme?"
                value={formData.draft_certificate_complying}
                onChange={(v) => setFormData({ ...formData, draft_certificate_complying: v })}
                disabled={!isTechnicalReviewer || technicalReviewCompleted}
              />
              <YesNoSelector
                label="Has the Regulatory Compliance Assessment been made?"
                value={formData.regulatory_compliance_made}
                onChange={(v) => setFormData({ ...formData, regulatory_compliance_made: v })}
                disabled={!isTechnicalReviewer || technicalReviewCompleted}
              />
              <YesNoSelector
                label="Are audit findings satisfactory?"
                value={formData.audit_findings_satisfactory}
                onChange={(v) => setFormData({ ...formData, audit_findings_satisfactory: v })}
                disabled={!isTechnicalReviewer || technicalReviewCompleted}
              />
              <YesNoSelector
                label="Is closure of the nonconformity of audit sufficient?"
                value={formData.nonconformity_closure_sufficient}
                onChange={(v) => setFormData({ ...formData, nonconformity_closure_sufficient: v })}
                disabled={!isTechnicalReviewer || technicalReviewCompleted}
              />
              <div className="space-y-2">
                <Label>Reasons if No:</Label>
                <Textarea
                  value={formData.reasons_if_no}
                  onChange={(e) => setFormData({ ...formData, reasons_if_no: e.target.value })}
                  placeholder="Hayır cevabı verdiyseniz nedenlerini yazın..."
                  disabled={!isTechnicalReviewer || technicalReviewCompleted}
                />
              </div>
              <Separator />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Technical Reviewer Name</Label>
                  <Input
                    value={formData.technical_reviewer_name}
                    onChange={(e) => setFormData({ ...formData, technical_reviewer_name: e.target.value })}
                    disabled={!isTechnicalReviewer || technicalReviewCompleted}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={formData.technical_reviewer_date}
                    onChange={(e) => setFormData({ ...formData, technical_reviewer_date: e.target.value })}
                    disabled={!isTechnicalReviewer || technicalReviewCompleted}
                  />
                </div>
              </div>
              
              {/* Teknik İnceleme Kaydet Butonu */}
              {isTechnicalReviewer && !technicalReviewCompleted && (
                <div className="flex justify-end pt-4">
                  <Button onClick={handleSaveTechnicalReview} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Teknik İncelemeyi Tamamla
                  </Button>
                </div>
              )}
              {technicalReviewCompleted && (
                <div className="flex items-center gap-2 text-green-600 pt-4">
                  <CheckCircle2 className="h-5 w-5" />
                  <span>Teknik İnceleme {fr48Data?.technical_reviewer_name} tarafından {fr48Data?.technical_reviewer_date} tarihinde tamamlandı.</span>
                </div>
              )}
            </CardContent>
          </Card>
            </TabsContent>

            {/* Komite Kararı Sekmesi */}
            <TabsContent value="committee" className="space-y-6">
              {!isCommitteeMember && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-amber-800">
                      <AlertCircle className="h-5 w-5" />
                      <span>Bu bölümü düzenlemek için Belgelendirme Komitesi yetkisi gereklidir.</span>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {!technicalReviewCompleted && (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-red-800">
                      <AlertCircle className="h-5 w-5" />
                      <span>Komite kararı verebilmek için önce Teknik İnceleme tamamlanmalıdır.</span>
                    </div>
                  </CardContent>
                </Card>
              )}

          {/* Komite Kriterleri */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5" />
                Committee Acceptability Criteria
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <YesNoSelector
                label="Is the audit file prepared as described in the Auditor's Guide?"
                value={formData.audit_file_prepared}
                onChange={(v) => setFormData({ ...formData, audit_file_prepared: v })}
                disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted}
              />
              <YesNoSelector
                label="Do reports and proofs demonstrate conformity assessment?"
                value={formData.reports_demonstrate_conformity}
                onChange={(v) => setFormData({ ...formData, reports_demonstrate_conformity: v })}
                disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted}
              />
              <YesNoSelector
                label="Do reports demonstrate no conflict of interest?"
                value={formData.reports_demonstrate_effectiveness}
                onChange={(v) => setFormData({ ...formData, reports_demonstrate_effectiveness: v })}
                disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted}
              />
              <YesNoSelector
                label="Is corrective action efficient for detected nonconformities?"
                value={formData.corrective_action_efficient}
                onChange={(v) => setFormData({ ...formData, corrective_action_efficient: v })}
                disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted}
              />
              <YesNoSelector
                label="Has organization closed all nonconformities?"
                value={formData.nonconformities_closed}
                onChange={(v) => setFormData({ ...formData, nonconformities_closed: v })}
                disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted}
              />
              <YesNoSelector
                label="Has organization fulfilled all GSTC requirements?"
                value={formData.requirements_fulfilled}
                onChange={(v) => setFormData({ ...formData, requirements_fulfilled: v })}
                disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted}
              />
            </CardContent>
          </Card>

          {/* Denetçi Performansı */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Auditor Performance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Auditor Auditing Performance</Label>
                  <Select
                    value={formData.auditor_auditing_performance}
                    onValueChange={(v) => setFormData({ ...formData, auditor_auditing_performance: v })}
                    disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="appropriate">Appropriate</SelectItem>
                      <SelectItem value="inappropriate">Inappropriate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Objective Evidences in Report</Label>
                  <Select
                    value={formData.objective_evidences}
                    onValueChange={(v) => setFormData({ ...formData, objective_evidences: v })}
                    disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="appropriate">Appropriate</SelectItem>
                      <SelectItem value="inappropriate">Inappropriate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>IAF MD22 Compliance</Label>
                  <Select
                    value={formData.iaf_md_compliance}
                    onValueChange={(v) => setFormData({ ...formData, iaf_md_compliance: v })}
                    disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="appropriate">Appropriate</SelectItem>
                      <SelectItem value="inappropriate">Inappropriate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.performance_notes}
                  onChange={(e) => setFormData({ ...formData, performance_notes: e.target.value })}
                  disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted}
                />
              </div>
            </CardContent>
          </Card>

          {/* Kararlar */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Decision</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Certification</Label>
                  <Select
                    value={formData.certification_decision || ""}
                    onValueChange={(v) => setFormData({ ...formData, certification_decision: v === "_empty" ? null : v })}
                    disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seçin..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_empty">-- Boş --</SelectItem>
                      <SelectItem value="acceptable">Acceptable</SelectItem>
                      <SelectItem value="unacceptable">Unacceptable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Recertification</Label>
                  <Select
                    value={formData.recertification_decision || ""}
                    onValueChange={(v) => setFormData({ ...formData, recertification_decision: v === "_empty" ? null : v })}
                    disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seçin..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_empty">-- Boş --</SelectItem>
                      <SelectItem value="acceptable">Acceptable</SelectItem>
                      <SelectItem value="unacceptable">Unacceptable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label className="text-lg font-semibold">Result</Label>
                <RadioGroup
                  value={formData.result}
                  onValueChange={(v) => setFormData({ ...formData, result: v })}
                  className="flex gap-6"
                  disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="successful" id="successful" disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted} />
                    <Label htmlFor="successful" className="flex items-center gap-1 text-green-600">
                      <CheckCircle2 className="h-4 w-4" />
                      SUCCESSFUL
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="unsuccessful" id="unsuccessful" disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted} />
                    <Label htmlFor="unsuccessful" className="flex items-center gap-1 text-red-600">
                      <XCircle className="h-4 w-4" />
                      UNSUCCESSFUL
                    </Label>
                  </div>
                </RadioGroup>
              </div>
              
              <div className="space-y-2">
                <Label>Explanation</Label>
                <Textarea
                  value={formData.result_explanation}
                  onChange={(e) => setFormData({ ...formData, result_explanation: e.target.value })}
                  placeholder="Karar açıklaması..."
                  disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted}
                />
              </div>
            </CardContent>
          </Card>

          {/* Komite Üyesi */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Member of Certification Committee</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={formData.committee_member_name}
                    onChange={(e) => setFormData({ ...formData, committee_member_name: e.target.value })}
                    disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={formData.committee_member_date}
                    onChange={(e) => setFormData({ ...formData, committee_member_date: e.target.value })}
                    disabled={!isCommitteeMember || !technicalReviewCompleted || committeeReviewCompleted}
                  />
                </div>
              </div>
              
              {/* Komite Kararı Kaydet Butonu */}
              {isCommitteeMember && technicalReviewCompleted && !committeeReviewCompleted && (
                <div className="flex justify-end pt-4">
                  <Button onClick={handleSaveCommitteeReview} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Gavel className="h-4 w-4 mr-2" />}
                    Komite Kararını Onayla
                  </Button>
                </div>
              )}
              {committeeReviewCompleted && (
                <div className={`flex items-center gap-2 pt-4 ${fr48Data?.status === "approved" ? "text-green-600" : "text-red-600"}`}>
                  {fr48Data?.status === "approved" ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                  <span>
                    Komite Kararı: {fr48Data?.status === "approved" ? "ONAYLANDI" : "REDDEDİLDİ"} 
                    {fr48Data?.committee_members?.[0]?.name && ` - ${fr48Data.committee_members[0].name}`}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
            </TabsContent>
          </Tabs>

          {/* Genel Butonlar */}
          <div className="flex justify-end gap-4">
            <Button variant="outline" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Taslak Kaydet
            </Button>
            <Button onClick={handleDownload} disabled={downloading}>
              {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileDown className="h-4 w-4 mr-2" />}
              İndir (DOCX)
            </Button>
          </div>
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            FR.48 formunu doldurmak için yukarıdan bir denetim seçin
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FR48DecisionPage;
